/**
 * FileUploader.tsx
 *
 * Componente genérico de carga de archivos basado en react-dropzone.
 * Agnóstico al módulo — el diseño visual lo define el render prop `children`.
 *
 * Soporta cualquier variante visual:
 *   - Avatar: muestra el avatar actual como trigger de click/drop
 *   - Área: zona de drag-and-drop con borde punteado
 *   - Inline: botón simple para abrir el file picker
 *   - Editor de texto enriquecido: trigger embebido en toolbar
 *
 * Gestiona la creación y limpieza de object URLs para previews locales.
 *
 * @example
 * // Variante avatar
 * <FileUploader
 *   accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] }}
 *   maxSize={2 * 1024 * 1024}
 *   onFilesAccepted={(files, previewUrls) => handleFile(files[0], previewUrls[0])}
 * >
 *   {({ getRootProps, getInputProps, open }) => (
 *     <div {...getRootProps()} className="cursor-pointer">
 *       <input {...getInputProps()} />
 *       <UserAvatar src={previewUrl} name={name} color={color} size="xl" />
 *       <button type="button" onClick={open}>Cambiar foto</button>
 *     </div>
 *   )}
 * </FileUploader>
 *
 * @example
 * // Variante área
 * <FileUploader accept={{ 'image/*': [] }} onFilesAccepted={handleFiles} multiple>
 *   {({ getRootProps, getInputProps, isDragActive }) => (
 *     <div {...getRootProps()} className={isDragActive ? 'bg-primary/10' : ''}>
 *       <input {...getInputProps()} />
 *       <p>{isDragActive ? 'Suelta aquí' : 'Arrastra o haz clic para subir'}</p>
 *     </div>
 *   )}
 * </FileUploader>
 */

import { useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import type { Accept, FileRejection, DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';

/* ── Tipos públicos ─────────────────────────────────────────── */

/**
 * Bag de props que el render prop `children` recibe.
 * Expone las utilidades de react-dropzone más la función `open` para
 * disparar el file picker programáticamente desde cualquier elemento.
 */
export interface FileUploaderBag {
  /** Props para el contenedor root del dropzone */
  getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
  /** Props para el input file oculto */
  getInputProps: (props?: DropzoneInputProps) => DropzoneInputProps;
  /** true mientras el usuario arrastra un archivo sobre el área */
  isDragActive: boolean;
  /** true si el archivo arrastrado es rechazado (tipo/tamaño) */
  isDragReject: boolean;
  /** Abre el file picker nativo del sistema operativo */
  open: () => void;
}

export interface FileUploaderProps {
  /** Tipos MIME aceptados. Formato: { 'image/*': ['.jpg', '.png'] } */
  accept?: Accept;
  /** Tamaño máximo en bytes. Default: 2 MB */
  maxSize?: number;
  /** Permite seleccionar múltiples archivos. Default: false */
  multiple?: boolean;
  /** Deshabilita toda interacción */
  disabled?: boolean;
  /**
   * Callback cuando los archivos son aceptados.
   * Recibe el array de File y las object URLs de preview correspondientes.
   * Las URLs se revocan automáticamente al desmontar o en la siguiente selección.
   */
  onFilesAccepted: (files: File[], previewUrls: string[]) => void;
  /** Callback cuando archivos son rechazados por tipo o tamaño */
  onFilesRejected?: (rejections: FileRejection[]) => void;
  /** Render prop — define la presentación visual del uploader */
  children: (bag: FileUploaderBag) => React.ReactNode;
}

/* ── Componente ─────────────────────────────────────────────── */

export function FileUploader({
  accept,
  maxSize = 2 * 1024 * 1024,
  multiple = false,
  disabled = false,
  onFilesAccepted,
  onFilesRejected,
  children,
}: FileUploaderProps) {
  const previewUrlsRef = useRef<string[]>([]);

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    // Revoca las URLs previas para evitar memory leaks
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));

    if (acceptedFiles.length > 0) {
      const previewUrls = acceptedFiles.map((file) => URL.createObjectURL(file));
      previewUrlsRef.current = previewUrls;
      onFilesAccepted(acceptedFiles, previewUrls);
    } else {
      previewUrlsRef.current = [];
    }

    if (rejectedFiles.length > 0) {
      onFilesRejected?.(rejectedFiles);
    }
  };

  // Limpieza al desmontar
  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    accept,
    maxSize,
    multiple,
    disabled,
    onDrop,
  });

  return <>{children({ getRootProps, getInputProps, isDragActive, isDragReject, open })}</>;
}
